import React, { useState, useEffect } from 'react';
import XIcon from '../../icons/X.svg';


interface AnimatedPopupProps {
  isVisible: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  shouldBackgroundClickClose?: boolean;
  scrollable?: boolean;
  fitContent?: boolean;
  containerClassName?: string;
  contentClassName?: string;
  animationDurationMs?: number;
  closeOnEscape?: boolean;
  /** Rendered in the header row on the left (same row as the close X) */
  headerLeftContent?: React.ReactNode;
  /** Centred modal on every breakpoint instead of a bottom sheet on mobile */
  centered?: boolean;
  /** Overrides the shared close-button id so each popup's close is trackable on its own. */
  closeButtonId?: string;
}

const AnimatedPopup: React.FC<AnimatedPopupProps> = ({
  isVisible,
  onClose,
  title,
  children,
  shouldBackgroundClickClose = true,
  containerClassName = '',
  contentClassName = '',
  animationDurationMs = 500,
  closeOnEscape = false,
  headerLeftContent,
  centered = false,
  closeButtonId = 'animated-popup-close-btn',
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationClass, setAnimationClass] = useState('');

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setTimeout(() => {
        setAnimationClass('animate-fade-in-up');
      }, 10);
    } else if (shouldRender) {
      setAnimationClass('animate-fade-out-down');
      setTimeout(() => {
        setShouldRender(false);
        setAnimationClass('');
      }, animationDurationMs);
    }
  }, [isVisible, shouldRender, animationDurationMs]);

  useEffect(() => {
    if (!isVisible || !closeOnEscape) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, closeOnEscape, onClose]);

  // Disable body scroll when popup is visible
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  const handleBackgroundClick = () => {
    if (shouldBackgroundClickClose) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!shouldRender) {
    return null;
  }

  // The backdrop fades with the sheet over the same duration. Open is a keyframe animation (like the
  // sheet's): a transition would need a painted opacity-0 frame first, and the class flips 10ms after
  // mount, before the browser has one. Close is a transition, since by then the painted state exists.
  const isOpen = animationClass === 'animate-fade-in-up';

  return (
    <div
      id="animated-popup-overlay"
      className={`fixed top-0 left-0 w-full h-full bg-slate-500/75 z-[1000] flex justify-center ${centered ? 'items-center' : 'items-end md:items-center'} transition-opacity ease-in-out ${isOpen ? 'animate-fade-in-2 opacity-100' : 'opacity-0'}`}
      style={{ animationDuration: `${animationDurationMs}ms`, transitionDuration: `${animationDurationMs}ms` }}
      onClick={handleBackgroundClick}
    >
      <div
        id="animated-popup-container"
        className={`relative bg-white shadow-2xl overflow-hidden opacity-0 translate-y-full ${centered ? 'w-[90%] max-w-lg rounded-xl' : 'w-full rounded-t-xl md:rounded-xl md:w-[90%] md:max-w-lg'} ${animationClass} ${containerClassName}`}
        style={{ animationDuration: `${animationDurationMs}ms` }}
        onClick={handleContentClick}
      >

          <div id="animated-popup-header" className="w-full flex flex-row h-8 items-center justify-between px-3 pt-3 min-h-[2.5rem]">

            {/* Left slot - same row as close (e.g. favorite heart) */}
            <div className="flex items-center justify-start w-6 h-6 shrink-0">
              {headerLeftContent}
            </div>

            {title && (
              <div 
                id="animated-popup-title" 
                className="flex items-center justify-center bg-[#269C6E] max-w-[300px] text-center text-white font-extrabold text-sm px-3 p-2 rounded-b-3xl absolute left-1/2 -translate-x-1/2"
              >
                {title}
              </div>
            )}

            {/* Close button - always visible */}
            <button 
              id={closeButtonId} 
              className="w-6 h-6 p-0 bg-transparent border-none cursor-pointer z-10 shrink-0"
              onClick={() => {
                onClose()
              }}
            >
              <img id="animated-popup-close-icon" src={XIcon} alt="Close" className="w-6 h-6" />
            </button>

          </div>

        {/* Content */}
        <div
          id="animated-popup-content"
          className={`overflow-y-auto p-4 md:p-6 md:pt-4 max-h-[100vh] ${contentClassName}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default AnimatedPopup;
